import { DEFAULT_RESOURCE_PANEL_CONFIG } from "@/game/config/resourcePanelConfig";
import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import { tilerepoRoadsFrameNames } from "@/game/entities/config";
import type { ResourceType } from "@/game/entities/config/resources";
import { settlementNames } from "@/game/entities/config/settlements";
import type { UpgradeId } from "@/game/entities/config/upgrades";
import type { PlagueTile } from "@/game/PlagueRepository";
import useTileRepository from "@/hooks/useTileRepository";
import type { SocialMediaType } from "@/libs/AppCommunicatior";
import Communicator from "@/libs/AppCommunicatior";
import { type Timestamp } from "@/types";
import type { SettlementItem, TileCoordinates } from "@/types/tile";
import { IconTypes } from "@/types/ui";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { Translator } from "@/utils/translate";
import { defineStore } from "pinia";
import { computed, type ComputedRef } from "vue";

export type ActiveProvision = {
	storeId: string;
	expiresAt: Date;
};

export type UserStoreState = {
	userName: string;
	userEmail: string;
	userId: string;
	country: string;
	language: string;
	linkedEmail: boolean;
	linkedSocialMediaType: Array<SocialMediaType>;
	// user stats
	playerLevel: number;
	townsConquered: number;
	townsDiscovered: number;
	totalTownLevels: number;
	totalWorkforce: number;
	totalFoodProduction: number;
	tilesExplored: number;
	forestsOnExploredTiles: number;
	exploredWaterTiles: number;
	roadsAndBridges: number;
	completedUpgrades: number;
	superClickersUsed: number;
	accountEstablished: Date;
	lastLogin: Date;
	lastTickBefore: number;
	showPrivacyOptionsSettings: boolean;
	newUpgradesIds: Array<UpgradeId>;
	inGameGemPurchaseForCoinsOfferId: number;
	isAdPlaying: boolean;
	appRated: boolean;
	appRatedGemRewardClaimed: boolean;
	discordRewardClaimed: boolean;
	upgradeFilterVisibility: IconTypes.HIDE | IconTypes.SHOW;
	gemMinesClaimable: boolean;
	lastSeenNewsTimestamp: Date | null;
	lastDailyRewardPickedDaysAfterReg: number;
	daysAfterRegistration: number;
	nameChanged: boolean;
	marketplaceTimestamps: Record<string, Timestamp>;
	upgrade6NerfFixedv2: boolean; // TODO: to be removed in future, when all users with upgrade 6 lvl > 0, have this true
	appliedUpgradeRetrogrants: Array<string>; // one-shot free upgrade grant ids already applied for this account
	incomeBoostsUsedToday: number;
	incomeBoostTicksRemaining: number;
	activeSpecialLocationsIds: Array<number>;
	revealedSettlementFromOfferCoordinates: TileCoordinates | null;
	isPayingCustomer: boolean;
	isDailyFreeEventPackClaimed: Date | null;
	isDailyFreeRegularPackClaimed: Date | null;
	adRewardsCount: number;
	last_tick_hash?: string;
	appliedEventGeneration: number;
	plagueCoordinates: Array<PlagueTile> | null;
	abTestGroup: string | null;
	resourcePanelConfig: Array<ResourceType | string>;
	activeProvisions: Array<ActiveProvision>;
	deeplinkWelcomeBackOfferExpires: Date | null;
	isWelcomeBackRewardClaimed: boolean;
};

type UserStore = {
	$getPayload: () => UserStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: Partial<UserStoreState>) => void;
	userName: ComputedRef<string>;
	userEmail: ComputedRef<string>;
	userId: ComputedRef<string>;
	country: ComputedRef<string>;
	language: ComputedRef<string>;
	linkedEmail: ComputedRef<boolean>;
	linkedSocialMediaType: ComputedRef<Array<SocialMediaType>>;
	playerLevel: ComputedRef<number>;
	townsConquered: ComputedRef<number>;
	townsDiscovered: ComputedRef<number>;
	totalTownLevels: ComputedRef<number>;
	totalWorkforce: ComputedRef<number>;
	totalFoodProduction: ComputedRef<number>;
	tilesExplored: ComputedRef<number>;
	forestsOnExploredTiles: ComputedRef<number>;
	exploredWaterTiles: ComputedRef<number>;
	roadsAndBridges: ComputedRef<number>;
	completedUpgrades: ComputedRef<number>;
	superClickersUsed: ComputedRef<number>;
	accountEstablished: ComputedRef<Date>;
	lastLogin: ComputedRef<Date>;
	lastTickBefore: ComputedRef<number>;
	showPrivacyOptionsSettings: ComputedRef<boolean>;
	newUpgradesIds: ComputedRef<Array<UpgradeId>>;
	inGameGemPurchaseForCoinsOfferId: ComputedRef<number>;
	isAdPlaying: ComputedRef<boolean>;
	appRated: ComputedRef<boolean>;
	appRatedGemRewardClaimed: ComputedRef<boolean>;
	discordRewardClaimed: ComputedRef<boolean>;
	upgradeFilterVisibility: ComputedRef<IconTypes.HIDE | IconTypes.SHOW>;
	gemMinesClaimable: ComputedRef<boolean>;
	lastSeenNewsTimestamp: ComputedRef<Date | null>;
	lastDailyRewardPickedDaysAfterReg: ComputedRef<number>;
	daysAfterRegistration: ComputedRef<number>;
	nameChanged: ComputedRef<boolean>;
	marketplaceTimestamps: ComputedRef<Record<string, Timestamp>>;
	upgrade6NerfFixedv2: ComputedRef<boolean>;
	appliedUpgradeRetrogrants: ComputedRef<Array<string>>;
	incomeBoostsUsedToday: ComputedRef<number>;
	incomeBoostTicksRemaining: ComputedRef<number>;
	activeSpecialLocationsIds: ComputedRef<Array<number>>;
	revealedSettlementFromOfferCoordinates: ComputedRef<TileCoordinates | null>;
	isPayingCustomer: ComputedRef<boolean>;
	isDailyFreeEventPackClaimed: ComputedRef<Date | null>;
	isDailyFreeRegularPackClaimed: ComputedRef<Date | null>;
	adRewardsCount: ComputedRef<number>;
	appliedEventGeneration: ComputedRef<number>;
	plagueCoordinates: ComputedRef<Array<PlagueTile> | null>;
	abTestGroup: ComputedRef<string | null>;
	resourcePanelConfig: ComputedRef<Array<ResourceType | "">>;
	activeProvisions: ComputedRef<Array<ActiveProvision>>;
	isRoyalProvisionActive: ComputedRef<boolean>;
	deeplinkWelcomeBackOfferExpires: ComputedRef<Date | null>;
	isWelcomeBackRewardClaimed: ComputedRef<boolean>;
	clearProvisions: () => void;
	addProvision(storeId: string, durationMs: number): void;
	removeProvision(storeId: string): void;
	cleanExpiredProvisions: () => boolean;
	setUserName(name: string): void;
	setUserEmail(email: string): void;
	setCountry(country: string): void;
	setLanguage(language: string): void;
	setLinkedEmail(value: boolean): void;
	setShowPrivacyOptionsSettings(show: boolean): void;
	setNewUpgradesIds(ids: Array<UpgradeId>): void;
	setInGameGemPurchaseForCoinsOfferId(offerId: number): void;
	setIsAdPlaying(value: boolean): void;
	setAppRated(value: boolean): void;
	setAppRatedGemRewardClaimed(value: boolean): void;
	setGemMinesClaimable(value: boolean): void;
	setDiscordRewardClaimed(value: boolean): void;
	setExploredTiles(value: number): void;
	setPlayerLevel(level: number): void;
	setUpgradeFilterVisibility(
		visibility: IconTypes.HIDE | IconTypes.SHOW,
	): void;
	setLastSeenNewsTimestamp: (value: Date) => void;
	setLastDailyRewardPickedDaysAfterReg: (value: number) => void;
	setMarketplaceTimestamps(pack: string, timestamp: Timestamp): void;
	setUpgrade6NerfFixedv2: (value: boolean) => void;
	markUpgradeRetrograntApplied: (retrograntId: string) => void;
	setIncomeBoostsUsedToday: (value: number) => void;
	setIncomeBoostTicksRemaining: (value: number) => void;
	setActiveSpecialLocationsIds: (value: Array<number>) => void;
	setRevealedSettlementFromOfferCoordinates: (
		coordinates: TileCoordinates | null,
	) => void;
	setSuperClickersUsed: (value: number) => void;
	setIsDailyFreeEventPackClaimed: () => void;
	setIsDailyFreeRegularPackClaimed: () => void;
	setAdRewardsCount: (value: number) => void;
	setTownsConquered: () => void;
	setRoadsAndBridges: () => void;
	setAppliedEventGeneration(value: number): void;
	setPlagueCoordinates(coordinates: Array<PlagueTile> | null): void;
	setAbTestGroup(group: string): void;
	setResourcePanelConfig(config: Array<ResourceType | "">): void;
	setDeeplinkWelcomeBackOfferExpires(expires: Date | null): void;
	setWelcomeBackRewardClaimed(value: boolean): void;
};

type UserStoreDiffAction = {
	// type: "set";
	key: keyof UserStoreState;
	currentValue: UserStoreState[keyof UserStoreState];
	value: UserStoreState[keyof UserStoreState];
	// reason?: string | false;
	timestamp: Timestamp;
};

type UserStorePayload = {
	state: UserStoreState;
	actions: Array<UserStoreDiffAction>;
};

const STORE_NAME = "user";
const STORED_EVENT_GENERATION_KEY = "event.appliedEventGeneration";

const readStoredEventGeneration = (): number => {
	try {
		const raw = localStorage.getItem(STORED_EVENT_GENERATION_KEY);
		if (raw === null) return 0;

		const parsed = +raw;
		return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
	} catch {
		return 0;
	}
};

const writeStoredEventGeneration = (value: number): void => {
	try {
		if (!Number.isFinite(value)) return;
		localStorage.setItem(
			STORED_EVENT_GENERATION_KEY,
			Math.max(0, value).toString(),
		);
	} catch {
		// Ignore storage failures, keep in-memory state working.
	}
};

export default defineStore(STORE_NAME, (): UserStore => {
	// Immutable state can never be changed directly,
	// but can be replaced with a new version of the state containing the desired changes.
	const state = createImmutableState<UserStoreState>({
		userName: "",
		userEmail: "",
		userId: "",
		country: "en",
		language: "en",
		linkedEmail: true,
		linkedSocialMediaType: [],
		// TODO: user stats
		playerLevel: 1,
		townsConquered: 0,
		townsDiscovered: 0,
		totalTownLevels: 0,
		totalWorkforce: 0,
		totalFoodProduction: 0,
		tilesExplored: 0,
		forestsOnExploredTiles: 0,
		exploredWaterTiles: 0,
		roadsAndBridges: 0,
		completedUpgrades: 0,
		superClickersUsed: 0,
		accountEstablished: new Date("June 1, 2025 03:24:00"), // TODO nahradit spravnou hodnotou z BE,
		lastLogin: new Date(),
		lastTickBefore: 10 * 60 * 1_000, // 10 minut - TODO nahradit spravnou hodnotou z be
		showPrivacyOptionsSettings: false,
		newUpgradesIds: [],
		gemMinesClaimable: false,
		// NOTE: The BE automatically saves basic data types, but not complex ones like arrays and objects.
		// That's why we use the following for storing the in-game market:
		inGameGemPurchaseForCoinsOfferId: 0,
		isAdPlaying: false,
		appRated: false,
		appRatedGemRewardClaimed: false,
		discordRewardClaimed: false,
		upgradeFilterVisibility: IconTypes.SHOW,
		lastSeenNewsTimestamp: null,
		nameChanged: false,
		lastDailyRewardPickedDaysAfterReg: 0,
		daysAfterRegistration: 0,
		marketplaceTimestamps: {},
		upgrade6NerfFixedv2: false,
		appliedUpgradeRetrogrants: [],
		incomeBoostsUsedToday: 0,
		incomeBoostTicksRemaining: 0,
		activeSpecialLocationsIds: [],
		revealedSettlementFromOfferCoordinates: null,
		isPayingCustomer: false,
		isDailyFreeEventPackClaimed: null,
		isDailyFreeRegularPackClaimed: null,
		adRewardsCount: 0,
		appliedEventGeneration: readStoredEventGeneration(),
		plagueCoordinates: null,
		abTestGroup: null,
		resourcePanelConfig: DEFAULT_RESOURCE_PANEL_CONFIG,
		activeProvisions: [],
		deeplinkWelcomeBackOfferExpires: null,
		isWelcomeBackRewardClaimed: false,
	});

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): UserStoreState => {
		cleanExpiredProvisions();

		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<keyof UserStoreState>;
		return keys.reduce(
			(
				acc: UserStoreState,
				key: keyof UserStoreState,
			): UserStoreState => ({
				...acc,
				[key]: data[key],
			}),
			{} as UserStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $actions = new Array<UserStoreDiffAction>();

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getActions = (): Array<UserStoreDiffAction> =>
		$actions.splice(0, $actions.length);

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: UserStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): UserStorePayload | null => {
		if (!$isPatched) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
			actions: $getActions(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: Partial<UserStoreState>): void => {
		const userState = value as Partial<UserStoreState>;

		setImmutableState(
			state,
			Object.assign(userState, {
				accountEstablished:
					userState.accountEstablished instanceof Date
						? userState.accountEstablished
						: new Date(userState.accountEstablished!),
				lastLogin:
					userState.lastLogin instanceof Date
						? userState.lastLogin
						: new Date(userState.lastLogin!),
			}),
		);

		if (typeof userState.appliedEventGeneration === "number") {
			const next = Math.max(
				state.value.appliedEventGeneration,
				userState.appliedEventGeneration,
			);
			setImmutableStateProperty(state, "appliedEventGeneration", next);
			writeStoredEventGeneration(next);
		}

		setTimeout((): void => {
			$isPatched = true;
		}, 0);
	};

	const userName = getImmutableStateProperty(state, "userName", "");

	const userEmail = getImmutableStateProperty(state, "userEmail", "");

	const userId = getImmutableStateProperty(
		state,
		"userId",
		"dsa456s4-ds45-ga54-daa755fdw465", // TODO nahradit spravnou hodnout z BE
	);

	const country = getImmutableStateProperty(state, "country", "en");

	const language = getImmutableStateProperty(state, "language", "en");
	const linkedEmail = getImmutableStateProperty(state, "linkedEmail", false);
	const linkedSocialMediaType = getImmutableStateProperty(
		state,
		"linkedSocialMediaType",
		[],
	);
	const playerLevel = getImmutableStateProperty(state, "playerLevel", 0);

	const townsConquered = getImmutableStateProperty(
		state,
		"townsConquered",
		0,
	);
	const townsDiscovered = getImmutableStateProperty(
		state,
		"townsDiscovered",
		0,
	);
	const totalTownLevels = getImmutableStateProperty(
		state,
		"totalTownLevels",
		0,
	);
	const totalWorkforce = getImmutableStateProperty(
		state,
		"totalWorkforce",
		0,
	);
	const totalFoodProduction = getImmutableStateProperty(
		state,
		"totalFoodProduction",
		0,
	);
	const tilesExplored = getImmutableStateProperty(state, "tilesExplored", 0);
	const forestsOnExploredTiles = getImmutableStateProperty(
		state,
		"forestsOnExploredTiles",
		0,
	);
	const exploredWaterTiles = getImmutableStateProperty(
		state,
		"exploredWaterTiles",
		0,
	);
	const roadsAndBridges = getImmutableStateProperty(
		state,
		"roadsAndBridges",
		0,
	);
	const completedUpgrades = getImmutableStateProperty(
		state,
		"completedUpgrades",
		0,
	);
	const superClickersUsed = getImmutableStateProperty(
		state,
		"superClickersUsed",
		0,
	);
	const accountEstablished = getImmutableStateProperty(
		state,
		"accountEstablished",
		new Date("June 1, 2025 03:24:00"), // TODO nahradit spravnou hodnotou z BE,
	);
	const lastLogin = getImmutableStateProperty(state, "lastLogin", new Date());

	const lastTickBefore = getImmutableStateProperty(
		state,
		"lastTickBefore",
		10 * 60 * 1_000,
	);

	// Filters only those provisions that have not yet expired
	const activeProvisions = computed((): Array<ActiveProvision> => {
		const rawProvisions = getImmutableStateProperty(
			state,
			"activeProvisions",
			[],
		).value;
		const now = Date.now();
		return rawProvisions.filter(
			(p: ActiveProvision): boolean =>
				new Date(p.expiresAt).getTime() > now,
		);
	});

	const isRoyalProvisionActive = computed(
		(): boolean => activeProvisions.value.length > 0,
	);

	const showPrivacyOptionsSettings = getImmutableStateProperty(
		state,
		"showPrivacyOptionsSettings",
		false,
	);
	const newUpgradesIds = getImmutableStateProperty(
		state,
		"newUpgradesIds",
		[],
	);

	const inGameGemPurchaseForCoinsOfferId = getImmutableStateProperty(
		state,
		"inGameGemPurchaseForCoinsOfferId",
		0,
	);

	const isAdPlaying = getImmutableStateProperty(state, "isAdPlaying", false);

	const appRated = getImmutableStateProperty(state, "appRated", false);
	const appRatedGemRewardClaimed = getImmutableStateProperty(
		state,
		"appRatedGemRewardClaimed",
		false,
	);

	const gemMinesClaimable = getImmutableStateProperty(
		state,
		"gemMinesClaimable",
		false,
	);

	const discordRewardClaimed = getImmutableStateProperty(
		state,
		"discordRewardClaimed",
		false,
	);

	const upgradeFilterVisibility = getImmutableStateProperty(
		state,
		"upgradeFilterVisibility",
		IconTypes.SHOW,
	);
	const nameChanged = getImmutableStateProperty(state, "nameChanged", false);

	const lastSeenNewsTimestamp = getImmutableStateProperty(
		state,
		"lastSeenNewsTimestamp",
		null,
	);

	const lastDailyRewardPickedDaysAfterReg = getImmutableStateProperty(
		state,
		"lastDailyRewardPickedDaysAfterReg",
		0,
	);

	const daysAfterRegistration = getImmutableStateProperty(
		state,
		"daysAfterRegistration",
		0,
	);

	const marketplaceTimestamps = getImmutableStateProperty(
		state,
		"marketplaceTimestamps",
		{},
	);

	const upgrade6NerfFixedv2 = getImmutableStateProperty(
		state,
		"upgrade6NerfFixedv2",
		false,
	);

	const appliedUpgradeRetrogrants = getImmutableStateProperty(
		state,
		"appliedUpgradeRetrogrants",
		[] as Array<string>,
	);

	const incomeBoostsUsedToday = getImmutableStateProperty(
		state,
		"incomeBoostsUsedToday",
		0,
	);

	const incomeBoostTicksRemaining = getImmutableStateProperty(
		state,
		"incomeBoostTicksRemaining",
		0,
	);

	const activeSpecialLocationsIds = getImmutableStateProperty(
		state,
		"activeSpecialLocationsIds",
		[],
	);

	const revealedSettlementFromOfferCoordinates = getImmutableStateProperty(
		state,
		"revealedSettlementFromOfferCoordinates",
		null,
	);

	const isPayingCustomer = getImmutableStateProperty(
		state,
		"isPayingCustomer",
		false,
	);

	const isDailyFreeEventPackClaimed = getImmutableStateProperty(
		state,
		"isDailyFreeEventPackClaimed",
		null,
	);

	const isDailyFreeRegularPackClaimed = getImmutableStateProperty(
		state,
		"isDailyFreeRegularPackClaimed",
		null,
	);

	const adRewardsCount = getImmutableStateProperty(
		state,
		"adRewardsCount",
		0,
	);

	const appliedEventGeneration = getImmutableStateProperty(
		state,
		"appliedEventGeneration",
		0,
	);

	const plagueCoordinates = getImmutableStateProperty(
		state,
		"plagueCoordinates",
		null,
	);

	const abTestGroup = getImmutableStateProperty(state, "abTestGroup", null);

	const resourcePanelConfig = getImmutableStateProperty(
		state,
		"resourcePanelConfig",
		DEFAULT_RESOURCE_PANEL_CONFIG,
	);

	const deeplinkWelcomeBackOfferExpires = getImmutableStateProperty(
		state,
		"deeplinkWelcomeBackOfferExpires",
		null,
	);

	const isWelcomeBackRewardClaimed = getImmutableStateProperty(
		state,
		"isWelcomeBackRewardClaimed",
		false,
	);

	const setDeeplinkWelcomeBackOfferExpires = (
		deeplinkWelcomeBackOfferExpires: Date | null,
	): void => {
		setImmutableStateProperty(
			state,
			"deeplinkWelcomeBackOfferExpires",
			deeplinkWelcomeBackOfferExpires,
		);
	};

	const setWelcomeBackRewardClaimed = (
		isWelcomeBackRewardClaimed: boolean,
	): void => {
		setImmutableStateProperty(
			state,
			"isWelcomeBackRewardClaimed",
			isWelcomeBackRewardClaimed,
		);
	};

	const addProvision = (storeId: string, durationMs: number): void => {
		const current = [...state.value.activeProvisions];
		const now = Date.now();

		// Clean up already expired provisions from the list first (just to be safe)
		const activeOnly = current.filter(
			(p: ActiveProvision): boolean =>
				new Date(p.expiresAt).getTime() > now,
		);

		const existingIndex = activeOnly.findIndex(
			(p: ActiveProvision): boolean => p.storeId === storeId,
		);

		if (existingIndex !== -1) {
			// If package with this storeId is already active, do not accumulate time.
			// You can either ignore it or do nothing.
			return;
		}

		// If it's not active (either never added or already expired), add it fresh from now
		activeOnly.push({
			storeId,
			expiresAt: new Date(now + durationMs),
		});

		setImmutableStateProperty(state, "activeProvisions", activeOnly);
	};

	const removeProvision = (storeId: string): void => {
		const current = state.value.activeProvisions.filter(
			(p: ActiveProvision): boolean => p.storeId !== storeId,
		);
		setImmutableStateProperty(state, "activeProvisions", current);
	};

	const clearProvisions = (): void => {
		setImmutableStateProperty(state, "activeProvisions", []);
	};

	const cleanExpiredProvisions = (): boolean => {
		const rawProvisions = getImmutableStateProperty(
			state,
			"activeProvisions",
			[],
		).value;

		if (!rawProvisions || rawProvisions.length === 0) {
			return false;
		}

		const now = Date.now();
		const validProvisions = rawProvisions.filter(
			(p: ActiveProvision): boolean =>
				new Date(p.expiresAt).getTime() > now,
		);

		const hasChanged = validProvisions.length !== rawProvisions.length;

		if (hasChanged) {
			setImmutableStateProperty(
				state,
				"activeProvisions",
				validProvisions,
			);
		}

		return validProvisions.length > 0;
	};

	const setPlagueCoordinates = (
		coordinates: Array<PlagueTile> | null,
	): void => {
		setImmutableStateProperty(state, "plagueCoordinates", coordinates);
	};

	const setMarketplaceTimestamps = (
		pack: string,
		timestamp: Timestamp = Date.now(),
	): void =>
		setImmutableStateProperty(state, "marketplaceTimestamps", {
			...(state.value.marketplaceTimestamps || {}),
			[pack]: timestamp,
		});

	const setUserName = (name: string): void => {
		setImmutableStateProperty(state, "userName", name);
		setImmutableStateProperty(state, "nameChanged", true);
	};

	const setUserEmail = (email: string): void => {
		setImmutableStateProperty(state, "userEmail", email);
	};

	const setCountry = (country: string): void => {
		setImmutableStateProperty(state, "country", country);
	};

	const setLanguage = async (language: string): Promise<void> => {
		const languageChanged =
			await Translator.getInstance().changeLanguage(language);
		if (!languageChanged) return;
		Communicator.getInstance().setLanguage(language);
		setImmutableStateProperty(state, "language", language);
	};

	const setLinkedEmail = (value: boolean): void => {
		setImmutableStateProperty(state, "linkedEmail", value);
	};

	const setShowPrivacyOptionsSettings = (show: boolean): void => {
		setImmutableStateProperty(state, "showPrivacyOptionsSettings", show);
	};
	const setNewUpgradesIds = (ids: Array<UpgradeId>): void => {
		setImmutableStateProperty(state, "newUpgradesIds", ids);
	};

	const setInGameGemPurchaseForCoinsOfferId = (offerId: number): void => {
		setImmutableStateProperty(
			state,
			"inGameGemPurchaseForCoinsOfferId",
			offerId,
		);
	};

	const setIsAdPlaying = (value: boolean): void => {
		// eslint-disable-next-line no-console
		console.log("setIsAdPlaying called:", value); // TODO DS testing
		setImmutableStateProperty(state, "isAdPlaying", value);
	};

	const setAppRated = (value: boolean): void => {
		setImmutableStateProperty(state, "appRated", value);
	};

	const setAppRatedGemRewardClaimed = (value: boolean): void => {
		setImmutableStateProperty(state, "appRatedGemRewardClaimed", value);
	};

	const setDiscordRewardClaimed = (value: boolean): void => {
		setImmutableStateProperty(state, "discordRewardClaimed", value);
	};

	const setExploredTiles = (tilesExplored: number): void => {
		setImmutableStateProperty(state, "tilesExplored", tilesExplored);
	};

	const setPlayerLevel = (level: number): void => {
		setImmutableStateProperty(state, "playerLevel", level);
	};

	const setUpgradeFilterVisibility = (
		visibility: IconTypes.HIDE | IconTypes.SHOW,
	): void => {
		setImmutableStateProperty(state, "upgradeFilterVisibility", visibility);
	};

	const setGemMinesClaimable = (value: boolean): void => {
		setImmutableStateProperty(state, "gemMinesClaimable", value);
	};

	const setLastSeenNewsTimestamp = (value: Date): void => {
		setImmutableStateProperty(state, "lastSeenNewsTimestamp", value);
	};

	const setLastDailyRewardPickedDaysAfterReg = (value: number): void => {
		setImmutableStateProperty(
			state,
			"lastDailyRewardPickedDaysAfterReg",
			value,
		);
	};

	const setUpgrade6NerfFixedv2 = (value: boolean): void => {
		setImmutableStateProperty(state, "upgrade6NerfFixedv2", value);
	};

	const markUpgradeRetrograntApplied = (retrograntId: string): void => {
		const current = appliedUpgradeRetrogrants.value;
		if (current.includes(retrograntId)) return;
		setImmutableStateProperty(state, "appliedUpgradeRetrogrants", [
			...current,
			retrograntId,
		]);
	};

	const setIncomeBoostsUsedToday = (value: number): void => {
		setImmutableStateProperty(state, "incomeBoostsUsedToday", value);
	};

	const setIncomeBoostTicksRemaining = (value: number): void => {
		setImmutableStateProperty(state, "incomeBoostTicksRemaining", value);
	};

	const setActiveSpecialLocationsIds = (value: Array<number>): void => {
		setImmutableStateProperty(state, "activeSpecialLocationsIds", value);
	};

	const setRevealedSettlementFromOfferCoordinates = (
		coordinates: TileCoordinates | null,
	): void => {
		setImmutableStateProperty(
			state,
			"revealedSettlementFromOfferCoordinates",
			coordinates,
		);
	};

	const setSuperClickersUsed = (value: number): void => {
		setImmutableStateProperty(state, "superClickersUsed", value);
	};

	const setAdRewardsCount = (value: number): void => {
		setImmutableStateProperty(state, "adRewardsCount", value);
	};

	const setIsDailyFreeEventPackClaimed = (): void => {
		setImmutableStateProperty(
			state,
			"isDailyFreeEventPackClaimed",
			new Date(Date.now()),
		);
	};

	const setIsDailyFreeRegularPackClaimed = (): void => {
		setImmutableStateProperty(
			state,
			"isDailyFreeRegularPackClaimed",
			new Date(Date.now()),
		);
	};

	const setTownsConquered = (): void => {
		const castleEntities = Array.from(settlementNames).reduce(
			(
				acc: Array<SettlementCastleBuildingTileEntity>,
				[, { x, y }]: [number, SettlementItem],
			): Array<SettlementCastleBuildingTileEntity> => {
				const index = useTileRepository().getTileIndexByTileCoordinates(
					{
						x,
						y,
					},
				);
				const entity = useTileRepository().getTileEntityByTileIndex(
					index,
				) as SettlementCastleBuildingTileEntity;
				if (entity?.isRevealed) acc.push(entity);
				return acc;
			},
			new Array<SettlementCastleBuildingTileEntity>(),
		);

		const conquered = castleEntities.reduce(
			(
				acc: number,
				entity: SettlementCastleBuildingTileEntity,
			): number => (entity.isActive ? acc + 1 : acc),
			0,
		);

		setImmutableStateProperty(state, "townsConquered", conquered);
	};

	const setRoadsAndBridges = (): void => {
		const tileRepo = useTileRepository();
		const roadsCount = tileRepo.getTileCount2(tilerepoRoadsFrameNames);

		setImmutableStateProperty(state, "roadsAndBridges", roadsCount);
	};

	const setAppliedEventGeneration = (value: number): void => {
		const next = Math.max(state.value.appliedEventGeneration, value);
		setImmutableStateProperty(state, "appliedEventGeneration", next);
		writeStoredEventGeneration(next);
		$isPatched = true;
		$payloadInProgress = null;
	};

	const setAbTestGroup = (group: string): void => {
		setImmutableStateProperty(state, "abTestGroup", group);
	};

	// (Object.keys(state.value) as Array<keyof UserStoreState>).forEach(
	// 	(key: keyof UserStoreState): void => {
	// 		watch(
	// 			(): UserStoreState[keyof UserStoreState] => state.value[key],
	// 			(
	// 				value: UserStoreState[keyof UserStoreState],
	// 				currentValue: UserStoreState[keyof UserStoreState],
	// 			): void => {
	// 				$actions.push({
	// 					key,
	// 					currentValue,
	// 					value,
	// 					timestamp: Date.now(),
	// 				});
	// 			},
	// 		);
	// 	},
	// );

	const setResourcePanelConfig = (
		config: Array<ResourceType | string>,
	): void => {
		setImmutableStateProperty(state, "resourcePanelConfig", config);
	};

	return {
		$getPayload,
		$resetPayload,
		$setState,
		userName,
		userEmail,
		userId,
		country,
		language,
		linkedEmail,
		linkedSocialMediaType,
		playerLevel,
		townsConquered,
		townsDiscovered,
		totalTownLevels,
		totalWorkforce,
		totalFoodProduction,
		tilesExplored,
		forestsOnExploredTiles,
		exploredWaterTiles,
		roadsAndBridges,
		completedUpgrades,
		superClickersUsed,
		accountEstablished,
		lastLogin,
		lastTickBefore,
		showPrivacyOptionsSettings,
		newUpgradesIds,
		inGameGemPurchaseForCoinsOfferId,
		isAdPlaying,
		appRated,
		appRatedGemRewardClaimed,
		discordRewardClaimed,
		upgradeFilterVisibility,
		lastSeenNewsTimestamp,
		gemMinesClaimable,
		nameChanged,
		lastDailyRewardPickedDaysAfterReg,
		daysAfterRegistration,
		marketplaceTimestamps,
		upgrade6NerfFixedv2,
		appliedUpgradeRetrogrants,
		incomeBoostsUsedToday,
		incomeBoostTicksRemaining,
		activeSpecialLocationsIds,
		revealedSettlementFromOfferCoordinates,
		isPayingCustomer,
		isDailyFreeEventPackClaimed,
		isDailyFreeRegularPackClaimed,
		adRewardsCount,
		appliedEventGeneration,
		plagueCoordinates,
		abTestGroup,
		resourcePanelConfig,
		activeProvisions,
		setUserName,
		setUserEmail,
		setCountry,
		setLanguage,
		setLinkedEmail,
		deeplinkWelcomeBackOfferExpires,
		isWelcomeBackRewardClaimed,
		setShowPrivacyOptionsSettings,
		setNewUpgradesIds,
		setInGameGemPurchaseForCoinsOfferId,
		setIsAdPlaying,
		setAppRated,
		setAppRatedGemRewardClaimed,
		setDiscordRewardClaimed,
		setExploredTiles,
		setPlayerLevel,
		setUpgradeFilterVisibility,
		setGemMinesClaimable,
		setLastSeenNewsTimestamp,
		setLastDailyRewardPickedDaysAfterReg,
		setMarketplaceTimestamps,
		setUpgrade6NerfFixedv2,
		markUpgradeRetrograntApplied,
		setIncomeBoostsUsedToday,
		setIncomeBoostTicksRemaining,
		setActiveSpecialLocationsIds,
		setRevealedSettlementFromOfferCoordinates,
		setSuperClickersUsed,
		setIsDailyFreeEventPackClaimed,
		setIsDailyFreeRegularPackClaimed,
		setAdRewardsCount,
		setTownsConquered,
		setRoadsAndBridges,
		setAppliedEventGeneration,
		setPlagueCoordinates,
		setAbTestGroup,
		setResourcePanelConfig,
		addProvision,
		removeProvision,
		clearProvisions,
		cleanExpiredProvisions,
		isRoyalProvisionActive,
		setDeeplinkWelcomeBackOfferExpires,
		setWelcomeBackRewardClaimed,
	};
});
